import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useNavigate } from 'react-router-dom';
import { hashPassword, verifyPassword } from '../utils/auth';
import { writeLoginLog } from '../services/loginLogService';

interface User {
  id: string;
  username: string;
  email: string;
  phone?: string;
  role: 'admin' | 'user' | 'responsable' | 'pasteur';
  assignedReports?: string[];
  isActive?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password?: string) => Promise<User | void>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          const userData = JSON.parse(savedUser);
          // Verify user still exists in Firestore
          const q = query(collection(db, 'users'), where('email', '==', userData.email));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const freshData = querySnapshot.docs[0].data();
            // Si l'utilisateur a été désactivé, on force la déconnexion
            if (freshData.isActive === false) {
              localStorage.removeItem('user');
              navigate('/login');
            } else {
              setUser(userData);
            }
          } else {
            // User no longer exists in Firestore
            localStorage.removeItem('user');
            navigate('/login');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        localStorage.removeItem('user');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [navigate]);

  useEffect(() => {
    // Set up storage event listener for multi-tab synchronization
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user') {
        if (e.newValue) {
          setUser(JSON.parse(e.newValue));
        } else {
          setUser(null);
          navigate('/login');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    }
  }, [navigate]);

  const login = async (email: string, password?: string): Promise<User | void> => {
    let candidate: { id: string; data: any } | null = null;
    try {
      const q = query(collection(db, 'users'), where('email', '==', email));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('USER_NOT_FOUND');
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      candidate = { id: userDoc.id, data: userData };

      if (userData.isActive === false) {
        throw new Error('ACCOUNT_DISABLED');
      }

      if (password !== undefined) {
        const ok = await verifyPassword(password, userData.password);
        if (!ok) {
          throw new Error('INVALID_PASSWORD');
        }
      }

      const userToStore = {
        id: userDoc.id,
        ...userData,
      } as User;

      setUser(userToStore);
      localStorage.setItem('user', JSON.stringify(userToStore));

      // Log de succès
      void writeLoginLog({
        userId: userToStore.id,
        phone: userToStore.phone,
        userName: userToStore.username,
        userRole: userToStore.role,
        success: true,
      });

      return userToStore;
    } catch (err: any) {
      const reason: string =
        err?.message === 'ACCOUNT_DISABLED'
          ? 'Compte désactivé'
          : err?.message === 'INVALID_PASSWORD'
          ? 'Mot de passe incorrect'
          : err?.message === 'USER_NOT_FOUND'
          ? 'Utilisateur introuvable'
          : err?.message || 'Erreur inconnue';

      // Log d'échec (création + vérification des échecs consécutifs gérée dans le service)
      void writeLoginLog({
        userId: candidate?.id,
        phone: candidate?.data?.phone,
        userName: candidate?.data?.username,
        userRole: candidate?.data?.role,
        success: false,
        failureReason: reason,
      });

      throw err;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.clear(); // Clear all auth-related data
  };

  const updateProfile = async (data: Partial<User>) => {
    if (!user?.id) return;

    const userRef = doc(db, 'users', user.id);
    await updateDoc(userRef, data);
    
    const updatedUser = { ...user, ...data };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const changePassword = async (newPassword: string) => {
    if (!user?.id) {
      throw new Error('Utilisateur non connecté');
    }

    // Hasher le nouveau mot de passe
    const hashedPassword = await hashPassword(newPassword);
    
    // Mettre à jour dans Firestore
    const userRef = doc(db, 'users', user.id);
    await updateDoc(userRef, { password: hashedPassword });
    
    // Note: Pas besoin de mettre à jour localStorage car le mot de passe 
    // n'est jamais stocké côté client
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateProfile, changePassword, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}